import { useEffect, useRef } from 'react';
import type { Presentation } from '../../types';
import { DeepLinkCta } from './DeepLinkCta';

interface Props {
  p: Extract<Presentation, { type: 'state_choropleth' }>;
}

// State-centroid coordinates (lat, lon) for the 50 states + DC + 5 territories.
// Embedded inline rather than loaded as a static asset so the component works
// in test envs (jsdom) and degraded networks without a fetch round-trip.
const STATE_CENTROIDS: Record<string, [number, number]> = {
  AL: [32.806671, -86.79113],   AK: [61.370716, -152.404419],
  AZ: [33.729759, -111.431221], AR: [34.969704, -92.373123],
  CA: [36.116203, -119.681564], CO: [39.059811, -105.311104],
  CT: [41.597782, -72.755371],  DE: [39.318523, -75.507141],
  DC: [38.897438, -77.026817],  FL: [27.766279, -81.686783],
  GA: [33.040619, -83.643074],  HI: [21.094318, -157.498337],
  ID: [44.240459, -114.478828], IL: [40.349457, -88.986137],
  IN: [39.849426, -86.258278],  IA: [42.011539, -93.210526],
  KS: [38.5266,   -96.726486],  KY: [37.66814,  -84.670067],
  LA: [31.169546, -91.867805],  ME: [44.693947, -69.381927],
  MD: [39.063946, -76.802101],  MA: [42.230171, -71.530106],
  MI: [43.326618, -84.536095],  MN: [45.694454, -93.900192],
  MS: [32.741646, -89.678696],  MO: [38.456085, -92.288368],
  MT: [46.921925, -110.454353], NE: [41.12537,  -98.268082],
  NV: [38.313515, -117.055374], NH: [43.452492, -71.563896],
  NJ: [40.298904, -74.521011],  NM: [34.840515, -106.248482],
  NY: [42.165726, -74.948051],  NC: [35.630066, -79.806419],
  ND: [47.528912, -99.784012],  OH: [40.388783, -82.764915],
  OK: [35.565342, -96.928917],  OR: [44.572021, -122.070938],
  PA: [40.590752, -77.209755],  RI: [41.680893, -71.51178],
  SC: [33.856892, -80.945007],  SD: [44.299782, -99.438828],
  TN: [35.747845, -86.692345],  TX: [31.054487, -97.563461],
  UT: [40.150032, -111.862434], VT: [44.045876, -72.710686],
  VA: [37.769337, -78.169968],  WA: [47.400902, -121.490494],
  WV: [38.491226, -80.954456],  WI: [44.268543, -89.616508],
  WY: [42.755966, -107.30249],
  PR: [18.220833, -66.590149],  VI: [18.335765, -64.896335],
  GU: [13.444304, 144.793732],  AS: [-14.270972, -170.132217],
  MP: [15.0979,    145.6739],
};

const FALLBACK_CENTER: [number, number] = [39.0, -98.0]; // geographic US center
const FALLBACK_ZOOM = 4;

function radiusFor(value: number, max: number): number {
  // sqrt scaling — bubble area is proportional to value, which reads more
  // accurately than radius-proportional (Tufte). Min 8px so tiny values
  // stay clickable; max 36px so dense states (CA/TX) don't overlap badly.
  if (max <= 0) return 8;
  return Math.max(8, Math.min(36, Math.sqrt(value / max) * 36));
}

function colorFor(value: number, max: number): string {
  if (max <= 0) return '#cccccc';
  // 5-class sequential blue (USWDS-aligned). Using value/max bucket so
  // outliers (CA at 214 vs WY at 1) compress evenly.
  const ratio = value / max;
  if (ratio >= 0.8) return '#0b4778';   // darkest
  if (ratio >= 0.6) return '#1071c1';
  if (ratio >= 0.4) return '#2491e5';
  if (ratio >= 0.2) return '#73b3e7';
  return '#cfe8ff';                      // lightest
}

export function StateChoroplethPresentation({ p }: Props) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let map: { remove: () => void } | null = null;
    let cancelled = false;

    (async () => {
      if (!ref.current || typeof window === 'undefined') return;
      try {
        const L = (await import('leaflet')).default;
        if (cancelled || !ref.current) return;

        map = L.map(ref.current, {
          center: FALLBACK_CENTER,
          zoom: FALLBACK_ZOOM,
          scrollWheelZoom: false,
          zoomControl: true,
        });
        L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '&copy; OpenStreetMap contributors',
          maxZoom: 10,
          minZoom: 3,
        }).addTo(map as L.Map);

        const max = p.values.reduce((m, v) => Math.max(m, v.value), 0);
        // Collect markers in a feature group so we can fit the viewport to
        // the actual rendered states (e.g. "DMV vs national" should zoom
        // to the 3-state corridor; an Alaska-only result should zoom
        // to Alaska). Without this, the map stayed pinned at the
        // continental-US fallback regardless of result set.
        const markers: L.Layer[] = [];
        for (const item of p.values) {
          const code = item.state.toUpperCase();
          const center = STATE_CENTROIDS[code];
          if (!center) continue;
          const radius = radiusFor(item.value, max);
          const color = colorFor(item.value, max);
          const marker = L.circleMarker(center, {
            radius,
            color: '#1b1b1b',
            weight: 1,
            fillColor: color,
            fillOpacity: 0.85,
          });
          // Permanent label: state code + value layered on the marker.
          marker.bindTooltip(
            `<strong>${code}</strong><br/>${item.label ?? item.value.toLocaleString()}`,
            {
              permanent: true,
              direction: 'center',
              className: 'state-choropleth-label',
            },
          );
          marker.addTo(map as L.Map);
          markers.push(marker);
        }
        if (markers.length > 0) {
          try {
            const bounds = L.featureGroup(markers).getBounds();
            if (bounds.isValid()) {
              (map as L.Map).fitBounds(bounds, {
                // Asymmetric padding accounts for the bubble label
                // overhang on each marker (radius 8-36 px + tooltip).
                padding: [48, 48],
                // Cap so a single-state result doesn't zoom to street
                // level — keep enough context to read "this is FL"
                // rather than "this is a city in northern FL".
                maxZoom: 6,
                animate: false,
              });
            }
          } catch {
            // getBounds() throws on a degenerate group; the fallback
            // FALLBACK_CENTER/ZOOM constructor view is already correct
            // for that case (no states matched → show US overview).
          }
        }
      } catch (err) {
        if (ref.current) {
          ref.current.innerText = `Map unavailable: ${(err as Error).message}`;
        }
      }
    })();

    return () => {
      cancelled = true;
      map?.remove();
    };
  }, [p.values]);

  return (
    <div className="presentation-card">
      <div className="presentation-title">
        🗺 {p.title ?? 'By state'}
        {p.metric_label && <span className="presentation-subtitle"> — {p.metric_label}</span>}
      </div>
      <div
        ref={ref}
        className="state-choropleth-canvas"
        style={{ height: 420 }}
        role="img"
        aria-label={`Map of US states colored by ${p.metric_label ?? 'value'}, with ${p.values.length} states shown.`}
      />
      {p.cta && <DeepLinkCta cta={p.cta} />}
    </div>
  );
}
